import { signal } from '@/lib/telemetry'
import { ALL_HELP_KEYS } from '@/lib/therapistHelps'

/**
 * Typed signal helpers for the THERAPIST web build — the counterpart to the
 * patient-side `telemetry-events.ts`. Kept in its own module so it stays out
 * of the native patient bundle (App.vue dynamic-imports it inside the embed
 * branch) and imports nothing patient-specific.
 *
 * These signals reach a *separate* TelemetryDeck app (see
 * `VITE_TELEMETRYDECK_APP_ID_THERAPIST`), so domain events keep their natural
 * `exercise.*` / `homework.*` / `patient.*` namespaces — they can't collide
 * with the patient dashboard. Only the generic app-lifecycle events carry a
 * `therapist.` prefix so they stay distinguishable if a dashboard is ever
 * merged.
 *
 * Privacy: never send exercise names, topic names, patient names/idents, or
 * item words — only opaque ids (hashed by the SDK) and low-cardinality
 * categorical filter values.
 *
 * Naming convention mirrors the patient file: `category.action`, snake_case
 * props.
 */

export type ExerciseSource = 'themenbox' | 'datenbank'

// --- App lifecycle -----------------------------------------------------

export function trackTherapistAppLaunched(): void {
  signal('therapist.app.launched')
}

export function trackTherapistAppReady(): void {
  signal('therapist.app.ready')
}

/**
 * The embed handshake never completed: the iframe sent `embed:ready` but the
 * portal never answered with `embed:hello` within the watchdog window (the
 * "stuck on Verbinde mit Portal …" hang). `attempts` is how many `embed:ready`
 * announces went unanswered — a non-trivial count points at the portal
 * attaching its listener late (after `await syncLinks()`).
 */
export function trackEmbedHandshakeStalled(attempts: number): void {
  signal('therapist.embed.handshake_stalled', { attempts })
}

/**
 * The self-healing re-announce paid off: a later `embed:ready` got through and
 * the handshake completed. Fired only when it took more than one announce, so
 * the count quantifies how often the race actually bites in production.
 */
export function trackEmbedHandshakeRecovered(attempts: number): void {
  signal('therapist.embed.handshake_recovered', { attempts })
}

// --- Help-key classification -------------------------------------------

export interface HelpCategoryFlags {
  helps_semantic: boolean
  helps_phonematic: boolean
  helps_graphematic: boolean
  helps_visual: boolean
}

/**
 * Fold the raw HilfestellungPicker keys (`semantic_1..3`, `phonematic_1..3`,
 * `graphematic_1..3`, `visual_mouth` / `visual_mouth_slow` / `visual_image`)
 * into one boolean per category. Lets the dashboard answer "which kinds of
 * help do therapists ever turn on" without sending the per-level keys.
 */
export function classifyHelps(keys: string[]): HelpCategoryFlags {
  return {
    helps_semantic: keys.some((k) => k.startsWith('semantic_')),
    helps_phonematic: keys.some((k) => k.startsWith('phonematic_')),
    helps_graphematic: keys.some((k) => k.startsWith('graphematic_')),
    helps_visual: keys.some((k) => k.startsWith('visual_')),
  }
}

// --- Exercise-creation snapshot ----------------------------------------

/**
 * The known draft defaults (see `stores/therapistExerciseDraft.ts`). A field
 * differing from its default is the "the therapist touched this" signal.
 */
const DEFAULT_ITEM_COUNT = 10
const DEFAULT_RATING_MODE = 'speech_recognition'

export interface CreateSnapshotInput {
  source: ExerciseSource
  itemCount: number
  ratingMode: string
  availableHelps: string[]
  itemsCustomized: boolean
  // Themenbox
  topicIds: number[]
  difficulties: number[]
  // Datenbank — every linguistic dimension is multi-select (empty = "alle").
  datenbank: {
    word_type: string[]
    word_frequency: string[]
    word_structure: string[]
    articulation_change: string[]
    syllable_count: string[]
    syllable_structure: string[]
    syllable_accent: string[]
    lautstruktur: { initial: string[]; medial: string[]; final: string[] } | null
  }
}

/**
 * Build the flat prop bag describing what the therapist configured. For every
 * field we send a `*_set` / `*_changed` boolean (so the dashboard can read
 * touch-rate without dealing with absence) and, for low-cardinality non-PII
 * categorical values, the chosen value itself (dropped by `signal()` when
 * undefined → reads as "never touched"). NEVER includes the exercise or topic
 * name. Pure function — takes plain values, not the Pinia store — so it's
 * trivially unit-testable.
 */
export function buildCreateSnapshotProps(
  s: CreateSnapshotInput,
): Record<string, unknown> {
  const helps = classifyHelps(s.availableHelps)
  const base: Record<string, unknown> = {
    source: s.source,
    item_count: s.itemCount,
    item_count_changed: s.itemCount !== DEFAULT_ITEM_COUNT,
    rating_mode: s.ratingMode,
    rating_mode_changed: s.ratingMode !== DEFAULT_RATING_MODE,
    helps_count: s.availableHelps.length,
    // Helps now default to ALL on, so "changed" means the therapist narrowed the
    // set (or cleared it) rather than simply having any selected.
    helps_changed: s.availableHelps.length !== ALL_HELP_KEYS.length,
    items_customized: s.itemsCustomized,
    ...helps,
  }

  if (s.source === 'themenbox') {
    return {
      ...base,
      topic_selected: s.topicIds.length > 0,
      difficulty_count: s.difficulties.length,
      // Low-cardinality (values 1..4); a sorted CSV lets the dashboard see the
      // exact combination chosen.
      difficulties: s.difficulties.slice().sort((a, b) => a - b).join(','),
      difficulty_changed: s.difficulties.length > 0,
    }
  }

  const db = s.datenbank
  const laut = db.lautstruktur
  // Each dimension is multi-select; send `*_set` (any value chosen) plus a sorted
  // CSV of the low-cardinality categorical values so the dashboard can read the
  // exact combination without per-field columns.
  const csv = (values: string[]): string | undefined =>
    values.length ? values.slice().sort().join(',') : undefined
  return {
    ...base,
    word_type_set: db.word_type.length > 0,
    word_type: csv(db.word_type),
    word_frequency_set: db.word_frequency.length > 0,
    word_frequency: csv(db.word_frequency),
    word_structure_set: db.word_structure.length > 0,
    word_structure: csv(db.word_structure),
    articulation_change_set: db.articulation_change.length > 0,
    articulation_change: csv(db.articulation_change),
    syllable_count_set: db.syllable_count.length > 0,
    syllable_count: csv(db.syllable_count),
    syllable_structure_set: db.syllable_structure.length > 0,
    syllable_structure: csv(db.syllable_structure),
    syllable_accent_set: db.syllable_accent.length > 0,
    syllable_accent: csv(db.syllable_accent),
    lautstruktur_used: laut !== null,
    phoneme_count_initial: laut ? laut.initial.length : 0,
    phoneme_count_medial: laut ? laut.medial.length : 0,
    phoneme_count_final: laut ? laut.final.length : 0,
  }
}

/**
 * True iff the therapist changed *any* field away from its default — the
 * one-glance "did they engage at all" flag for the abandon funnel. Derived
 * from a snapshot's `*_changed`/`*_set`/`*_used` booleans plus the two
 * always-meaningful flags (`items_customized`, `topic_selected`).
 *
 * Note: the `helps_<category>` flags are NOT counted — helps default to all-on,
 * so they're true in an untouched session. A genuine help edit surfaces through
 * `helps_changed` (an `*_changed` key), which is counted.
 */
function anyFieldTouched(snapshot: Record<string, unknown>): boolean {
  return Object.entries(snapshot).some(
    ([k, v]) =>
      v === true &&
      (k.endsWith('_changed') ||
        k.endsWith('_set') ||
        k === 'lautstruktur_used' ||
        k === 'items_customized' ||
        k === 'topic_selected'),
  )
}

// --- Exercise-creation funnel ------------------------------------------

// Note: the chooser screen itself isn't instrumented — its navigation buttons
// route declaratively, and each destination path view fires
// `exercise.create.started` with the same `path` prop on mount, which answers
// "which creation mode do therapists prefer" without a separate event.

export function trackExerciseCreateStarted(path: ExerciseSource): void {
  signal('exercise.create.started', { path })
}

export function trackExerciseCreateSaved(opts: {
  snapshot: Record<string, unknown>
  patientContext: boolean
  autoAssigned: boolean
  assignedAsHomework: boolean
}): void {
  signal('exercise.create.saved', {
    ...opts.snapshot,
    patient_context: opts.patientContext,
    auto_assigned: opts.autoAssigned,
    assigned_as_homework: opts.assignedAsHomework,
  })
}

export function trackExerciseCreateAbandoned(
  snapshot: Record<string, unknown>,
): void {
  signal('exercise.create.abandoned', {
    ...snapshot,
    any_field_touched: anyFieldTouched(snapshot),
  })
}

export function trackExerciseCreateDuplicateName(path: ExerciseSource): void {
  signal('exercise.create.duplicate_name', { path })
}

export function trackPhonemePickerOpened(): void {
  signal('exercise.create.phoneme_picker_opened')
}

export function trackPhonemePickerApplied(opts: {
  positions: number
  phonemeCount: number
}): void {
  signal('exercise.create.phoneme_picker_applied', {
    positions: opts.positions,
    phoneme_count: opts.phonemeCount,
  })
}

export function trackItemPickerOpened(path: ExerciseSource): void {
  signal('exercise.create.item_picker_opened', { path })
}

export function trackItemPickerApplied(selectedCount: number): void {
  signal('exercise.create.item_picker_applied', {
    selected_count: selectedCount,
  })
}

// --- Exercise management & assignment ----------------------------------

export function trackExerciseListViewed(exerciseCount: number): void {
  signal('exercise.list.viewed', { exercise_count: exerciseCount })
}

export function trackExerciseDeleted(): void {
  signal('exercise.deleted')
}

export function trackExerciseAssigned(opts: {
  isHomework: boolean
  fromCreate: boolean
}): void {
  signal('exercise.assigned', {
    is_homework: opts.isHomework,
    from_create: opts.fromCreate,
  })
}

export function trackExerciseUnassigned(): void {
  signal('exercise.unassigned')
}

export function trackHomeworkToggled(value: boolean): void {
  signal('homework.toggled', { value })
}

export function trackPatientSelected(caseloadSize: number): void {
  signal('patient.selected', { caseload_size: caseloadSize })
}
